import {
  format,
  formatDistance,
  isAfter,
  FormatDistanceOptions,
} from 'date-fns';
import { DATE_FORMAT, USER_FRIENDLY_DATE_FORMAT } from '../consts/date';

/**
 * Format a date. Opiniated about the default date format, but can be overridden.
 * @param date - The date to format
 * @returns The formatted date string
 */
export const formatDate = (
  date: string | Date,
  dateFormat: string = DATE_FORMAT,
) => {
  if (!date) {
    console.error('date is required');

    return '-';
  }

  const normalizedDate = new Date(date);
  const isDateValid = !isNaN(normalizedDate.getTime());

  if (!isDateValid) {
    console.error('Invalid date');

    return '-';
  }

  return format(normalizedDate, dateFormat);
};

/**
 * Get the distance between two dates. By default, if 'to' is not being sent, it will use the current date.
 * @param from - The date to start from
 * @param to - The date to end at
 * @returns The distance between the two dates
 */
export const getDistanceBetweenDates = (
  from: string,
  to?: string,
  options: FormatDistanceOptions = { addSuffix: true },
) => {
  if (!from) {
    console.error('from is required');

    return '';
  }

  if (to) {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const isFromAfterTo = isAfter(fromDate, toDate);

    if (isFromAfterTo) {
      console.error('"from" date cannot be after "to" date');

      return '';
    }
  }

  const fromDate = new Date(from);
  const toDate = to ? new Date(to) : new Date();

  const areBothDatesValid =
    !isNaN(fromDate.getTime()) && !isNaN(toDate.getTime());

  if (!areBothDatesValid) {
    console.error('Invalid date');

    return '';
  }

  return formatDistance(fromDate, toDate, options);
};

/**
 * Prepare date components for formatting.
 * @returns An object containing the formatted date components.
 */
export const prepareDateFormatting = (date: Date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');

  return { day, month, year, hour, minute };
};

/**
 * Get the current date and time formatted for filename use.
 * @returns The formatted date string in DD-MM-YYYY_HH-MM format
 */
export const getCurrentFormattedDate = (dateString?: string) => {
  const date = dateString ? new Date(dateString) : new Date();

  if (isNaN(date.getTime())) {
    console.error('Invalid date');

    return '';
  }

  const { day, month, year, hour, minute } = prepareDateFormatting(date);

  return `${day}-${month}-${year}${dateString ? ' ' : '_'}${hour}:${minute}`;
};

/**
 * Format a date in a user-friendly way.
 * @param date - The date to format (string or Date object)
 * @returns The formatted date string (e.g., "7 Nov 2024")
 */
export const formatUserFriendlyDate = (date: string | Date): string => {
  if (!date) {
    return '-';
  }

  const normalizedDate = new Date(date);

  if (isNaN(normalizedDate.getTime())) {
    return '-';
  }

  return format(normalizedDate, USER_FRIENDLY_DATE_FORMAT);
};
